import React, { useEffect, useState } from "react";
import { useLocation, useParams } from "react-router-dom";
import {
  convertToTimeZone,
  dateFormatter,
  downloadTicketPdfFile,
  getEventPosterUrl,
} from "../../utils/Helper";
import MainComponentWrapper from "../Common/MainComponentWrapper";
import GalleryModal from "../Common/Modal/GalleryModal";
import { showErrorToast } from "../../utils/notify";

const EventSection = ({
  details,
  eventDetials,
  loading,
  error,
  attendeeDetail,
  orderId: orderIdProp,
  showDownload = true,
  topClassName = "event-order-detail-top",
  eventCardClassName = "",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [timezone, setTimeZone] = useState(null);
  const [isDownloading, setIsDownloading] = useState(false);
  const [imageFailed, setImageFailed] = useState(false);
  const { orderId: orderIdParam } = useParams();
  const location = useLocation();
  const orderId = orderIdProp || orderIdParam;
  const posterFallback = "/event/assets/img/sellingevents.png";
  const galleryFiles =
    (Array.isArray(eventDetials?.mainimage) && eventDetials.mainimage.length
      ? eventDetials.mainimage
      : null) ||
    (Array.isArray(eventDetials?.gallery) && eventDetials.gallery.length
      ? eventDetials.gallery
      : null) ||
    (eventDetials?.mainimage ? [eventDetials.mainimage] : null) ||
    (eventDetials?.gallery ? [eventDetials.gallery] : null);
  const eventImageSrc = imageFailed
    ? posterFallback
    : getEventPosterUrl(eventDetials, posterFallback);

  const isSingleDay =
    details?.eventId?.eventTiming?.eventDayType === "single";
  const isMultiDay = details?.eventId?.eventTiming?.eventDayType === "multi";

  const locationText = [
    eventDetials?.location?.address,
    eventDetials?.location?.city,
    eventDetials?.location?.state,
    eventDetials?.location?.country,
    eventDetials?.location?.postalCode,
  ]
    .map((part) => String(part || "").trim())
    .filter(Boolean)
    .filter((part, index, parts) => {
      const lower = part.toLowerCase();
      return !parts.slice(0, index).some((prev) => prev.toLowerCase() === lower);
    })
    .join(", ");

  useEffect(() => {
    setTimeZone(
      convertToTimeZone(
        new Date(eventDetials?.startDate || new Date()),
        eventDetials?.timeZone || "Asia/Calcutta"
      )
    );
  }, [eventDetials]);

  useEffect(() => {
    setImageFailed(false);
  }, [eventDetials?.mainimage, eventDetials?.gallery]);

  const handleDownloadTicket = async (event) => {
    event.preventDefault();
    if (isDownloading) {
      return;
    }

    setIsDownloading(true);
    try {
      await downloadTicketPdfFile({
        orderId,
        attendeeId: attendeeDetail?._id,
        fileName: eventDetials?.title || "ticket",
      });
    } catch (downloadError) {
      console.log(downloadError);
      showErrorToast(downloadError?.message || "Unable to download ticket. Please try again.");
    } finally {
      setIsDownloading(false);
    }
  };

  const eventCardClasses = ["event-order-detail-event-card", eventCardClassName]
    .filter(Boolean)
    .join(" ");

  const canDownload =
    showDownload &&
    eventDetials?.isCancelled === false &&
    location.pathname !== `/myOrder/${orderId}` &&
    Boolean(attendeeDetail?._id);

  return (
    <MainComponentWrapper errorInfo={error} Loading={loading}>
      <div className={`row g-4 ${topClassName}`}>
        <div className="col-lg-6">
          <div className="event-order-detail-image-wrap">
            <img
              className="event-order-detail-image"
              data-pdf-role="event"
              src={eventImageSrc}
              alt={eventDetials?.title || "Event"}
              onClick={() => {
                if (galleryFiles && !imageFailed) {
                  setIsOpen(true);
                }
              }}
              onError={() => setImageFailed(true)}
              style={{
                cursor: galleryFiles && !imageFailed ? "pointer" : "default",
              }}
            />
            {galleryFiles && !imageFailed && (
              <GalleryModal
                filesData={galleryFiles}
                Open={isOpen}
                Index="0"
                onClose={() => setIsOpen(false)}
              />
            )}
          </div>
        </div>
        <div className="col-lg-6">
          <article className={eventCardClasses}>
            <h2 className="event-order-detail-event-name">
              {eventDetials?.title}
            </h2>
            <div className="event-order-detail-facts">
              {isSingleDay && (
                <div className="event-order-detail-fact">
                  <span className="event-order-detail-fact-icon" aria-hidden="true">
                    <i className="bi bi-calendar3"></i>
                  </span>
                  <div className="event-order-detail-fact-copy">
                    <span className="event-order-detail-fact-label">Date</span>
                    <span className="event-order-detail-fact-value">
                      {dateFormatter(eventDetials?.startDate)}
                    </span>
                  </div>
                </div>
              )}
              {isMultiDay && (
                <>
                  <div className="event-order-detail-fact">
                    <span
                      className="event-order-detail-fact-icon"
                      aria-hidden="true"
                    >
                      <i className="bi bi-calendar3"></i>
                    </span>
                    <div className="event-order-detail-fact-copy">
                      <span className="event-order-detail-fact-label">
                        Start Date
                      </span>
                      <span className="event-order-detail-fact-value">
                        {dateFormatter(
                          details?.eventId?.eventTiming?.startDate
                        )}
                      </span>
                    </div>
                  </div>
                  <div className="event-order-detail-fact">
                    <span
                      className="event-order-detail-fact-icon"
                      aria-hidden="true"
                    >
                      <i className="bi bi-calendar3"></i>
                    </span>
                    <div className="event-order-detail-fact-copy">
                      <span className="event-order-detail-fact-label">
                        End Date
                      </span>
                      <span className="event-order-detail-fact-value">
                        {dateFormatter(details?.eventId?.eventTiming?.endDate)}
                      </span>
                    </div>
                  </div>
                </>
              )}
              {!isSingleDay && !isMultiDay && eventDetials?.startDate && (
                <div className="event-order-detail-fact">
                  <span
                    className="event-order-detail-fact-icon"
                    aria-hidden="true"
                  >
                    <i className="bi bi-calendar3"></i>
                  </span>
                  <div className="event-order-detail-fact-copy">
                    <span className="event-order-detail-fact-label">
                      Start Date
                    </span>
                    <span className="event-order-detail-fact-value">
                      {dateFormatter(eventDetials?.startDate)}
                    </span>
                  </div>
                </div>
              )}
              <div className="event-order-detail-fact">
                <span className="event-order-detail-fact-icon" aria-hidden="true">
                  <i className="bi bi-clock"></i>
                </span>
                <div className="event-order-detail-fact-copy">
                  <span className="event-order-detail-fact-label">Time</span>
                  <span className="event-order-detail-fact-value">
                    {eventDetials?.startTime} - {eventDetials?.endTime} (
                    {timezone})
                  </span>
                </div>
              </div>
              <div className="event-order-detail-fact">
                <span className="event-order-detail-fact-icon" aria-hidden="true">
                  <i className="bi bi-geo-alt"></i>
                </span>
                <div className="event-order-detail-fact-copy">
                  <span className="event-order-detail-fact-label">Location</span>
                  <span className="event-order-detail-fact-value">
                    {locationText}
                  </span>
                </div>
              </div>
            </div>
            {canDownload && (
              <div className="event-ticket-detail-download-row no-print">
                <button
                  type="button"
                  className="event-ticket-detail-download-btn"
                  onClick={handleDownloadTicket}
                  disabled={isDownloading}
                  aria-label="Download ticket"
                >
                  <i
                    className={`bi ${
                      isDownloading ? "bi-arrow-repeat" : "bi-download"
                    }`}
                    aria-hidden="true"
                  ></i>
                  <span>{isDownloading ? "Preparing…" : "Download ticket"}</span>
                </button>
              </div>
            )}
          </article>
        </div>
      </div>
    </MainComponentWrapper>
  );
};

export default EventSection;
